/**
 * Rehydrate conversation Notes after reload from persisted user state.
 */

export const WORK_PANEL_KEYS = {
    SPACES: "spaces",
    BOARDS: "boards",
    ITEMS: "items",
};

/** @typedef {"none" | "activity_split" | "activity_drawer_work" | "activity_drawer_team" | "team" | "work"} DocumentOpenKind */

/**
 * @param {string|null|undefined} activePanel
 */
export function resolveWorkPanelFromActivePanel(activePanel) {
    if (
        activePanel === WORK_PANEL_KEYS.SPACES ||
        activePanel === WORK_PANEL_KEYS.BOARDS ||
        activePanel === WORK_PANEL_KEYS.ITEMS
    ) {
        return activePanel;
    }

    return WORK_PANEL_KEYS.ITEMS;
}

/** @type {readonly ("activity" | "team" | "work")[]} */
const DEFAULT_DOCUMENT_OPEN_STORE_ORDER = ["activity", "team", "work"];

/** @type {Record<string, readonly ("activity" | "team" | "work")[]>} */
const DOCUMENT_OPEN_STORE_ORDER_BY_ACTIVE_PAGE = {
    activity: ["activity", "team", "work"],
    team: ["team", "activity", "work"],
    work: ["work", "team", "activity"],
};

/**
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} activityStore
 * @returns {DocumentOpenKind | null}
 */
function documentOpenKindFromActivityStore(activityStore) {
    if (activityStore?.documentDetails?.open) {
        return "activity_split";
    }

    return null;
}

/**
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} teamPanelStore
 * @returns {DocumentOpenKind | null}
 */
function documentOpenKindFromTeamPanelStore(teamPanelStore) {
    if (teamPanelStore?.documentDetails?.open) {
        return teamPanelStore.documentDetails.is_activity_drawer_full_width
            ? "activity_drawer_team"
            : "team";
    }

    return null;
}

/**
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} workspaceStore
 * @returns {DocumentOpenKind | null}
 */
function documentOpenKindFromWorkspaceStore(workspaceStore) {
    if (workspaceStore?.documentDetails?.open) {
        return workspaceStore.documentDetails.is_activity_drawer_full_width
            ? "activity_drawer_work"
            : "work";
    }

    return null;
}

/**
 * @param {"activity" | "team" | "work"} storeKey
 * @param {object} stores
 * @returns {DocumentOpenKind | null}
 */
function documentOpenKindForStoreKey(storeKey, stores) {
    switch (storeKey) {
        case "activity":
            return documentOpenKindFromActivityStore(stores.activityStore);
        case "team":
            return documentOpenKindFromTeamPanelStore(stores.teamPanelStore);
        case "work":
            return documentOpenKindFromWorkspaceStore(stores.workspaceStore);
        default:
            return null;
    }
}

/**
 * @param {object} params
 * @param {boolean} params.isDocumentOpen
 * @param {string|null|undefined} params.activePage
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} params.activityStore
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} params.workspaceStore
 * @param {{ documentDetails?: { open?: boolean; is_activity_drawer_full_width?: boolean } }} params.teamPanelStore
 * @returns {DocumentOpenKind | null}
 */
export function resolveDocumentOpenKindFromStores({
    isDocumentOpen,
    activePage,
    activityStore,
    workspaceStore,
    teamPanelStore,
}) {
    if (!isDocumentOpen) {
        return null;
    }

    const storeOrder =
        DOCUMENT_OPEN_STORE_ORDER_BY_ACTIVE_PAGE[activePage ?? ""] ??
        DEFAULT_DOCUMENT_OPEN_STORE_ORDER;

    for (const storeKey of storeOrder) {
        const kind = documentOpenKindForStoreKey(storeKey, {
            activityStore,
            teamPanelStore,
            workspaceStore,
        });

        if (kind) {
            return kind;
        }
    }

    return null;
}

/**
 * Pure plan for tests — describes which store should reopen notes.
 *
 * @param {object|null|undefined} state Persisted user state payload
 * @returns {{ kind: DocumentOpenKind }}
 */
export function resolveRestoreOpenNotesPlan(state) {
    if (!state?.is_document_open || state.conversation_id == null) {
        return { kind: "none" };
    }

    const persistedKind = state.document_open_kind;
    if (
        persistedKind === "activity_split" ||
        persistedKind === "activity_drawer_work" ||
        persistedKind === "activity_drawer_team" ||
        persistedKind === "team" ||
        persistedKind === "work"
    ) {
        return { kind: persistedKind };
    }

    const activePage = state.active_page;

    if (activePage === "activity") {
        return { kind: "activity_split" };
    }

    if (activePage === "team") {
        return { kind: "team" };
    }

    if (activePage === "work") {
        return { kind: "work" };
    }

    return { kind: "none" };
}

/**
 * Work/team panel notes restore after the work layout is mounted.
 *
 * @param {{ kind: DocumentOpenKind }} plan
 * @returns {boolean}
 */
export function shouldDeferRestoreOpenNotesPlan(plan) {
    return (
        plan.kind !== "none" &&
        plan.kind !== "activity_split"
    );
}

/**
 * @param {object} state
 * @param {object} deps
 * @param {import('pinia').Store} deps.workspaceStore
 * @param {import('pinia').Store} deps.activityStore
 * @param {import('pinia').Store} deps.teamPanelStore
 * @param {string|null|undefined} deps.fallbackActivePanel
 */
export function applyRestoreOpenNotesFromUserState(
    state,
    {
        workspaceStore,
        activityStore,
        teamPanelStore,
        fallbackActivePanel,
    },
) {
    const plan = resolveRestoreOpenNotesPlan(state);

    if (plan.kind === "none") {
        return;
    }

    const conversationId = state.conversation_id;
    const activePanel = resolveWorkPanelFromActivePanel(
        state.active_panel ??
            fallbackActivePanel ??
            workspaceStore.active_panel,
    );

    switch (plan.kind) {
        case "activity_split":
            activityStore.toggleDocument(true, conversationId);
            break;
        case "activity_drawer_work":
            workspaceStore.toggleDocument(
                true,
                conversationId,
                activePanel,
                undefined,
                undefined,
                true,
                true,
            );
            break;
        case "activity_drawer_team":
            teamPanelStore.toggleDocument(true, conversationId, true, true);
            break;
        case "team":
            teamPanelStore.toggleDocument(true, conversationId);
            break;
        case "work":
            workspaceStore.toggleDocument(
                true,
                conversationId,
                activePanel,
                undefined,
                undefined,
                true,
            );
            break;
        default:
            break;
    }
}
